import { email } from '@gg/shared';

export type InviteField = { id: string; value: string };
/** A verdict per field: `sent`, or the reason the address was refused (invitations or teammates). */
export type FieldResults = Record<string, string | undefined>;

/**
 * Splits the invite fields before sending (decision 0006): `send` holds the filled fields with
 * a valid address and no verdict yet, `invalid` the filled ones that are not an email. Empty
 * fields are ignored, so one address out of three is enough. A field the server already
 * answered (sent, or refused with a reason) is skipped until it is edited, which clears the
 * verdict: resending it would get the same answer and use up one of the hourly sends.
 */
export function splitInviteFields(
  fields: InviteField[],
  results: FieldResults,
): { send: InviteField[]; invalid: string[] } {
  const send: InviteField[] = [];
  const invalid: string[] = [];
  for (const f of fields) {
    const value = f.value.trim();
    if (!value || results[f.id]) continue;
    if (email.safeParse(value).success) send.push({ id: f.id, value });
    else invalid.push(f.id);
  }
  return { send, invalid };
}

/**
 * Number of addresses sent when every filled field was sent, else null (decision 0007): the
 * form then leaves for the profile. A refused or invalid address keeps the participant on the
 * screen, with its reason, so it can be fixed.
 */
export function sentIfAllFilledSent(fields: InviteField[], results: FieldResults): number | null {
  const filled = fields.filter((f) => f.value.trim());
  if (filled.length === 0) return null;
  return filled.every((f) => results[f.id] === 'sent') ? filled.length : null;
}
